import { useState } from 'react';
import { Form } from 'formik';
import { FormField, FormikWithSchema } from '@gokrew/ui/form';
import { FormikSubmitButton } from '@gokrew/ui/components/FormikSubmitButton';
import { ArrowRight, Eye, EyeOff } from 'lucide-react';
import { LogoLoader } from '@gokrew/ui';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import type { ResetForgotPasswordForm } from '@gokrew/models/auth';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';
import { resetForgotPassword } from '@/api/auth';
import { resetForgotPasswordSchema } from '@gokrew/ui/validation/authSchema';
import type { ApiResponse } from '@gokrew/models/generic';
import { PasswordValidationIndicator } from '@gokrew/ui/components/PasswordValidationIndicator';
import styles from './components/AuthContent.module.css';

interface ResetPasswordFormikValues extends ResetForgotPasswordForm {
  confirmPassword: string;
}

export function ResetForgotPasswordPage() {
  const [showPass, setShowPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation(TranslationNS.Auth);
  const [params] = useSearchParams();

  const token = params.get('token');

  const initialValues: ResetPasswordFormikValues = {
    newPassword: '',
    token: token || '',
    confirmPassword: '',
  };

  // Reset password mutation
  const resetMutation = useMutation<
    ApiResponse,
    AxiosError<{ message: string }>,
    ResetForgotPasswordForm
  >({
    mutationFn: ({ token, newPassword }) => {
      if (!token) {
        throw new Error('Token is required for password reset.');
      }
      return resetForgotPassword(token, newPassword);
    },
    onSuccess: () => {
      toast.success(t('password_reset_success'));
      navigate('/login');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message ?? t('reset_failed'));
    },
  });

  return (
    <>
      <h2 className={styles.heading}>{t('reset_password')}</h2>
      <p className={styles.subtext}>{t('enter_new_password')}</p>

      <FormikWithSchema
        initialValues={initialValues}
        onSubmit={(values, { setSubmitting }) => {
          // Check if the token exists
          if (!token) {
            toast.error(t('invalid_token'));
            navigate('/login');
            setSubmitting(false);
            return;
          }
          resetMutation.mutate(
            {
              token: token!,
              newPassword: values.newPassword,
            },
            {
              onSettled: () => setSubmitting(false),
            }
          );
        }}
        validationSchema={resetForgotPasswordSchema}
      >
        {({ values }) => (
          <Form className={styles.form}>
            <FormField
              name="newPassword"
              type={showPass ? 'text' : 'password'}
              label={t('new_password')}
              placeholder={t('enter_new_password')}
              IconSuffix={showPass ? Eye : EyeOff}
              onIconClick={() => setShowPass(!showPass)}
            />

            <FormField
              name="confirmPassword"
              type={showConfirmPass ? 'text' : 'password'}
              label={t('confirm_password')}
              placeholder={t('confirm_password')}
              IconSuffix={showConfirmPass ? Eye : EyeOff}
              onIconClick={() => setShowConfirmPass(!showConfirmPass)}
            />

            <div className={styles.passwordRules}>
              <PasswordValidationIndicator
                password={values.newPassword}
                confirmPassword={values.confirmPassword}
                showMatchValidation={true}
              />
            </div>

            <FormikSubmitButton
              IconSuffix={resetMutation.isPending ? undefined : ArrowRight}
              disabled={resetMutation.isPending}
              className={styles.submitBtn}
            >
              {resetMutation.isPending ? (
                <LogoLoader size="sm" />
              ) : (
                t('reset_password')
              )}
            </FormikSubmitButton>

            <p className={styles.bottomRow}>
              {t('return_to')}{' '}
              <Link to="/login" className={styles.bottomLink}>
                {t('login')}
              </Link>
            </p>
          </Form>
        )}
      </FormikWithSchema>
    </>
  );
}
